import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useGenerationStore } from '../store/generationStore'
import { projectsApi, styleRefsApi } from '../lib/api'
import type { Project, StyleReference } from '../types'
import { AIStudio } from '../components/AIStudio'
import { ProjectCard } from '../components/ProjectCard'
import { GenerationCard } from '../components/GenerationCard'

export function Dashboard() {
  const { user } = useAuthStore()
  const { generations, loadingList, fetchGenerations, deleteGeneration } = useGenerationStore()
  const [projects, setProjects] = useState<Project[]>([])
  const [styleRefs, setStyleRefs] = useState<StyleReference[]>([])
  const [loadingProjects, setLoadingProjects] = useState(true)

  // Handle delete generation
  const handleDelete = async (generation: { id: string }) => {
    if (!confirm('Delete this generation? This cannot be undone.')) return
    try {
      await deleteGeneration(generation.id)
    } catch (err) {
      console.error('Failed to delete generation:', err)
    }
  }

  useEffect(() => {
    // Fetch all data with resilient error handling
    Promise.allSettled([
      fetchGenerations(),
      projectsApi.getAll().then(setProjects),
      styleRefsApi.getAll().then(setStyleRefs),
    ]).then((results) => {
      results.forEach((result, i) => {
        if (result.status === 'rejected') {
          console.error(`Dashboard fetch ${i} failed:`, result.reason)
        }
      })
      setLoadingProjects(false)
    })
  }, [fetchGenerations])

  return (
    <>
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 animate-fade-up stagger-1">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl md:text-4xl font-serif font-semibold text-text-main leading-tight">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-text-muted text-base">
            Create listing-ready product photos. No studio required.
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/settings?tab=billing" className="btn btn-secondary">
            <span className="material-symbols-outlined text-[20px]">add_circle</span>
            <span>Buy Credits</span>
          </Link>
          <Link to="/projects/new" className="btn btn-primary">
            <span className="material-symbols-outlined text-[20px]">add</span>
            <span>New Project</span>
          </Link>
        </div>
      </div>

      {/* AI Studio Card */}
      <div className="animate-fade-up stagger-2">
        <AIStudio styleRefs={styleRefs} />
      </div>

      {/* Recent Projects */}
      <div className="flex flex-col gap-5 animate-fade-up stagger-3">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-2xl font-serif font-medium flex items-center gap-2 text-text-main">
            Recent Projects
          </h2>
          <Link
            to="/projects"
            className="text-sm font-medium text-primary hover:text-primary-dark transition-colors flex items-center gap-1"
          >
            View All <span className="material-symbols-outlined text-sm">arrow_forward</span>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {loadingProjects ? (
            // Loading skeletons with shimmer
            [...Array(4)].map((_, i) => (
              <div
                key={i}
                className="aspect-[4/3] rounded-2xl skeleton-shimmer"
              />
            ))
          ) : (
            <>
              {projects.slice(0, 3).map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
              {/* Add New Project Card */}
              <Link
                to="/projects/new"
                className="aspect-[4/3] rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 flex flex-col items-center justify-center gap-3 hover:bg-primary/10 transition-all cursor-pointer group text-primary-dark"
              >
                <div className="bg-white p-3 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined text-2xl text-primary">
                    create_new_folder
                  </span>
                </div>
                <span className="font-medium text-sm">
                  Create New Folder
                </span>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Latest Generations */}
      <div className="flex flex-col gap-5 animate-fade-up stagger-4">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-2xl font-serif font-medium flex items-center gap-2 text-text-main">
            Recent Photos
          </h2>
          <div className="flex gap-2">
            <button className="size-9 flex items-center justify-center rounded-lg bg-white border border-border-color text-primary-dark shadow-sm hover:bg-gray-50">
              <span className="material-symbols-outlined text-sm">grid_view</span>
            </button>
            <button className="size-9 flex items-center justify-center rounded-lg bg-transparent hover:bg-white text-text-muted transition-colors">
              <span className="material-symbols-outlined text-sm">list</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {loadingList ? (
            // Loading skeletons with shimmer
            [...Array(5)].map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-xl skeleton-shimmer"
              />
            ))
          ) : generations.length === 0 ? (
            <div className="col-span-full text-center py-12 text-text-muted">
              <span className="material-symbols-outlined text-5xl mb-2 block">
                photo_library
              </span>
              <p>No photos yet. Create your first listing image above.</p>
            </div>
          ) : (
            generations.slice(0, 10).map((gen) => (
              <GenerationCard key={gen.id} generation={gen} onDelete={handleDelete} />
            ))
          )}
        </div>
      </div>
    </>
  )
}
